import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, KeyRound, Loader2, Mail, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const passwordRegex = /(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}/;

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.detail || err?.message || fallback;

const AdminForgotPassword = () => {
  const { admin, forgotPassword, verifyOtp, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(() => (
    localStorage.getItem("cynos_admin_password_reset_token") ? "password" :
    localStorage.getItem("cynos_admin_reset_token") ? "otp" :
    "email"
  ));
  const [email, setEmail] = useState(() => localStorage.getItem("cynos_admin_reset_email") || "");
  const [deliveryTarget, setDeliveryTarget] = useState(() => localStorage.getItem("cynos_admin_reset_delivery_target") || "");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Cynos Admin Password Recovery";
  }, []);

  const title = useMemo(() => {
    if (step === "otp") return "Verify OTP";
    if (step === "password") return "New Password";
    return "Forgot Password";
  }, [step]);

  if (admin) return <Navigate to="/admin" replace />;

  const requestOtp = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    const value = email.trim().toLowerCase();
    if (!emailRegex.test(value)) {
      setError("Enter a valid admin email address.");
      return;
    }

    setLoading(true);
    try {
      const data = await forgotPassword(value);
      const target = data?.delivery_target || "your registered admin email";
      setEmail(value);
      setDeliveryTarget(target);
      if (data?.dev_otp) {
        setMessage(`Email service is not configured. Local OTP: ${data.dev_otp}`);
      } else {
        setMessage(`OTP sent to ${target}.`);
      }
      setStep("otp");
    } catch (err) {
      setError(getErrorMessage(err, "Unable to send OTP."));
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!/^\d{6}$/.test(otp)) {
      setError("Enter the 6-digit OTP from your email.");
      return;
    }

    setLoading(true);
    try {
      await verifyOtp(otp);
      setMessage("OTP verified. Create your new password.");
      setStep("password");
    } catch (err) {
      setError(getErrorMessage(err, "Invalid OTP. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!passwordRegex.test(password)) {
      setError("Use at least 8 characters with uppercase, lowercase, and a number.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await resetPassword(password);
      setMessage("Password reset successfully. Please sign in.");
      setTimeout(() => navigate("/admin/login", { replace: true }), 1200);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to reset password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-cynos-bg" data-testid="admin-forgot-password-page">
      <div className="w-full max-w-sm bg-cynos-card border border-white/10 p-8">
        <div className="font-anton text-4xl uppercase">CYNOS<span className="text-cynos-red">.</span></div>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">Admin Recovery</p>

        <div className="mt-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center border border-white/10 bg-cynos-surface">
            {step === "email" && <Mail className="h-5 w-5 text-cynos-red" />}
            {step === "otp" && <ShieldCheck className="h-5 w-5 text-cynos-red" />}
            {step === "password" && <KeyRound className="h-5 w-5 text-cynos-red" />}
          </div>
          <div>
            <h1 className="font-anton text-3xl uppercase text-white">{title}</h1>
            <p className="mt-1 text-sm text-zinc-500">
              {step === "email" && "Recover access using your admin email."}
              {step === "otp" && `Enter the code sent to ${deliveryTarget || "your admin email"}.`}
              {step === "password" && "Choose a secure replacement password."}
            </p>
          </div>
        </div>

        {error && <div className="mt-5 text-cynos-red font-mono text-xs uppercase tracking-[0.18em]" data-testid="admin-recovery-error">{error}</div>}
        {message && (
          <div className="mt-5 flex items-start gap-2 text-emerald-300 font-mono text-xs uppercase tracking-[0.18em]" data-testid="admin-recovery-message">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{message}</span>
          </div>
        )}

        {step === "email" && (
          <form onSubmit={requestOtp} className="mt-6 space-y-4">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Admin Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 focus:border-white outline-none"
                data-testid="admin-recovery-email"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-cynos-red text-white py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-red-600 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="admin-recovery-send"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Sending OTP..." : "Send OTP"}
            </button>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={submitOtp} className="mt-6 space-y-4">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">6-Digit OTP</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 font-mono text-2xl tracking-[0.4em] focus:border-white outline-none"
                data-testid="admin-recovery-otp"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-cynos-red text-white py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-red-600 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="admin-recovery-verify"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={requestOtp}
              className="w-full border border-white/15 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 hover:border-white disabled:opacity-50"
            >
              Resend OTP
            </button>
          </form>
        )}

        {step === "password" && (
          <form onSubmit={submitPassword} className="mt-6 space-y-4">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">New Password</span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 focus:border-white outline-none"
                data-testid="admin-recovery-password"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Confirm Password</span>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 focus:border-white outline-none"
                data-testid="admin-recovery-confirm-password"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-cynos-red text-white py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-red-600 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="admin-recovery-reset"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        )}

        <div className="mt-6">
          <Link to="/admin/login" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white">
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminForgotPassword;
